import React, { useState, useEffect } from "react";
import {
  HashRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import {
  makeStyles,
  ThemeProvider as MaterialThemeProvider,
} from "@material-ui/core/styles";
import { AnonymousIdentity, Identity, Principal } from "@dfinity/agent";
import { ThemeProvider } from "styled-components";
import { Grid } from "@material-ui/core";
import { toast, ToastContainer } from "react-toastify";
import GlobalStyle from "@origyn-sa/common-ui/src/GlobalStyles.styled";
import { ReactComponent as WalletIcon } from "@origyn-sa/common-ui/src/assets/icons/wallet.svg";
import ProfileIcon from "@origyn-sa/common-ui/src/components/atoms/ProfileIcon";
import MenuItem from "@origyn-sa/common-ui/src/components/molecules/MenuItem";
import Menu from "@origyn-sa/common-ui/src/components/organisms/Menu";
import { ReactComponent as ExploreIcon } from "@origyn-sa/common-ui/src/assets/icons/explore.svg";
import { ReactComponent as ActivityIcon } from "@origyn-sa/common-ui/src/assets/icons/activity.svg";
import { ReactComponent as SettingsIcon } from "@origyn-sa/common-ui/src/assets/icons/settings.svg";
import { ReactComponent as CollectionsIcon } from "@origyn-sa/common-ui/src/assets/icons/collections.svg";
import { ReactComponent as ListingsIcon } from "@origyn-sa/common-ui/src/assets/icons/listings.svg";
import { ReactComponent as InventoryIcon } from "@origyn-sa/common-ui/src/assets/icons/inventory.svg";
import { ReactComponent as LogOutIcon } from "@origyn-sa/common-ui/src/assets/icons/logout.svg";
import { ReactComponent as InProgressIcon } from "@origyn-sa/common-ui/src/assets/icons/inProgress.svg";

import Container from "@origyn-sa/common-ui/src/components/atoms/Container";
import Profile from "@origyn-sa/common-ui/src/components/organisms/Profile";
import AppTheme, { MUITheme } from "@origyn-sa/common-ui/src/AppTheme.styled";
import MarketplaceExplore from "./pages/MarketplaceExplore";
import MarketplaceActivity from "./pages/MarketplaceActivity";
import MarketplaceCollections from "./pages/MarketplaceCollections";
import MarketplaceInventory from "./pages/MarketplaceInventory";
import MarketplaceSettings from "./pages/MarketplaceSettings";
import MarketplaceListings from "./pages/MarketplaceListings";
import MarketplaceSetup from "./pages/MarketplaceSetup";
import Claimable from "./pages/Claimable";
import { client, ICPClient } from "./agent";
import {
  getLastTokenUri,
  handleError,
  isDEBUG,
  ProductCardInput,
} from "./utils";
import NFTItem from "./pages/NFTItem";

const adminNavigation = [
  {
    text: "Settings",
    Icon: SettingsIcon,
    link: "/settings",
  },
  {
    text: "Collections",
    Icon: CollectionsIcon,
    link: "/collections",
  },
  {
    text: "Listings",
    Icon: ListingsIcon,
    link: "/listings",
  },
];

const profileNavigation = [
  {
    text: "My inventory",
    Icon: InventoryIcon,
    link: "/inventory",
  },
  {
    text: "My activity",
    Icon: ActivityIcon,
    link: "/activity",
  },
  {
    text: "Log out",
    Icon: LogOutIcon,
    link: "/connect",
    action: "logout",
  },
];

const containerStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
}));

const App = () => {
  console.log("MARKETAPP");
  const [loader, setLoader] = useState<boolean>(true);
  const [loggedInUser, setLoggedInUser] =
    useState<string | undefined>(undefined);
  const [admin, setAdmin] = useState<string | undefined>(undefined);

  const isAdmin = admin && admin == loggedInUser;
  const [firstLoad, setFirstLoad] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(true);

  useEffect(() => {
    refreshUser();
  }, []);

  const navigation = [
    // {
    //   text: "Marketplace",
    //   Icon: MarketplaceIcon,
    //   link: "/",
    // },
    { text: "Explore", Icon: ExploreIcon, link: "/explore" },
    {
      text: "Activity",
      Icon: ActivityIcon,
      link: "/activity",
    },
    {
      text: "Claimable",
      Icon: InProgressIcon,
      link: "/claimable",
    },
  ];

  if (loggedInUser) {
    navigation.push({
      text: "My Inventory",
      Icon: InventoryIcon,
      link: "/inventory",
    });
  }

  const refreshUser = async () => {
    await client.create(false);
    console.debug({
      client,
      authClient: client.authClient,
      actor: client.actor,
    });
    if (client.actor) {
      setLoader(true);
      // toast.dark("Refreshing user...");
      client.actor
        .whoami()
        .then((principal: Principal) => {
          console.debug({ principal });
          if (principal.toText() === "2vxsx-fae") {
            return;
          }
          setLoggedInUser(principal.toText());
        })
        .catch(handleError)
        .finally(() => {
          setFirstLoad(true);
          setLoader(false);
        });
      client.actor
        .isInitialized()
        .then((initialized: boolean) => {
          console.debug({ initialized });
          setInitialized(initialized);
        })
        .catch(handleError);

      client.actor
        .admin()
        .then((admin: Principal) => {
          console.debug({ admin });
          setAdmin(admin.toText());
        })
        .catch(handleError);
    }
  };

  const handleLogin = async () => {
    try {
      await client.create(true);
    } catch (e) {
      console.error("Failed to login", e);
      toast.error(`Failed to login : ${e}`);
    } finally {
      refreshUser();
    }
  };

  const handleLogout = () => {
    client.logout();
    setLoggedInUser(undefined);
  };

  return (
    <main className='App'>
      <Router>
        <ToastContainer />
        <ThemeProvider theme={AppTheme}>
          <MaterialThemeProvider theme={MUITheme}>
            <Grid container wrap='nowrap' className={containerStyles().root}>
              <Grid item>
                <Menu
                  logoClickHandler={() => window.location.reload()}
                  list={navigation}
                  adminList={isAdmin ? adminNavigation : []}
                />
              </Grid>
              <Grid item xs>
                <Container>
                  <Profile
                    userPrincipal={loggedInUser}
                    navigation={[
                      { text: "NFT Wallet", Icon: WalletIcon, link: "/" },
                    ]}
                    children={
                      loggedInUser ? (
                        <MenuItem
                          Icon={null}
                          label='Logout'
                          to='/'
                          rounded={false}
                          spacing={0}
                          onClick={handleLogout}
                        />
                      ) : (
                        <MenuItem
                          Icon={ProfileIcon}
                          label='Login'
                          to='/'
                          rounded={false}
                          spacing={0}
                          onClick={handleLogin}
                        />
                      )
                    }
                  />
                  <div className='pageContent'>
                    <Switch>
                      <Route exact path='/'>
                        <Redirect to='/explore' />
                      </Route>
                      <Route exact path='/market/:canisterId/:itemId'>
                        <NFTItem
                          loggedInUser={loggedInUser}
                          showTransfer={false}
                          showApproval={false}
                          showListing
                        />
                      </Route>
                      <Route exact path='/setup'>
                        <MarketplaceSetup />
                      </Route>
                      <Route exact path='/explore'>
                        {(initialized && <MarketplaceExplore />) || (
                          <Redirect to='/setup' />
                        )}
                      </Route>
                      <Route exact path='/activity'>
                        {(initialized && <MarketplaceActivity />) || (
                          <Redirect to='/setup' />
                        )}
                      </Route>
                      <Route exact path='/claimable'>
                        {(initialized && <Claimable />) || (
                          <Redirect to='/setup' />
                        )}
                      </Route>
                      <Route exact path='/collections'>
                        {(initialized && <MarketplaceCollections />) || (
                          <Redirect to='/setup' />
                        )}
                      </Route>
                      <Route exact path='/inventory'>
                        {(initialized && (
                          <MarketplaceInventory loggedInUser={loggedInUser} />
                        )) || <Redirect to='/setup' />}
                      </Route>
                      <Route exact path='/settings'>
                        {(initialized && <MarketplaceSettings />) || (
                          <Redirect to='/setup' />
                        )}
                      </Route>
                      <Route exact path='/listings'>
                        {(initialized && <MarketplaceListings />) || (
                          <Redirect to='/setup' />
                        )}
                      </Route>
                    </Switch>
                  </div>
                </Container>
              </Grid>
            </Grid>
          </MaterialThemeProvider>
        </ThemeProvider>
        <GlobalStyle color={AppTheme.palette.background2} />
      </Router>
    </main>
  );
};

export default App;
